<template>
  <b-modal id="bw-cloud-os-request-form" size="xl" @ok="sendRequest">
    <template #modal-title>
      {{ $t('components.requests.bw_cloud_os.title') }}
    </template>
    <b-alert :show="error_message !== null" variant="danger" dismissible>
      {{ error_message }}
    </b-alert>
    <b-overlay rounded="sm">
      <b-form>
        <b-alert show variant="warning">
          <b-row>
            <b-col cols="auto" class="my-auto">
              <netvs-icon icon="warning" size="2x"></netvs-icon>
            </b-col>
            <b-col class="h-100 my-auto">
              <p class="my-auto">{{ $t('components.requests.bw_cloud_os.permission_note') }}</p>
            </b-col>
          </b-row>
        </b-alert>
        <b-form-group :label="$t('global.enumeration.1', { msg: $t('components.requests.bw_cloud_os.project_name') })"
                      :description="$t('components.requests.bw_cloud_os.project_name_description')"
                      label-for="project_name">
          <b-form-input v-model="project_name"
                        :state="checkProjectName"
                        required
                        :placeholder="$t('components.requests.bw_cloud_os.project_name_placeholder')"/>
        </b-form-group>
        <b-form-group :label="$t('global.enumeration.2', { msg: $t('components.requests.bw_cloud_os.motivation') })"
                      :description="$t('components.requests.bw_cloud_os.motivation_description')"
                      label-for="motivation">
          <b-form-textarea
            :placeholder="$t('components.requests.all.placeholder_text')"
            :state="motivation.trim().length > 0"
            v-model="motivation"/>
        </b-form-group>
        <b-form-group :label="$t('global.enumeration.3', { msg: $t('components.requests.bw_cloud_os.vCPU') })"
                      :description="$t('components.requests.bw_cloud_os.vCPU_description')"
                      label-for="virtual_cpu_count">
          <b-form-input v-model="virtual_cpu_count" :state="virtual_cpu_count >= 1" type="number" min="1" step="1"
                        required/>
        </b-form-group>
        <b-form-group :label="$t('global.enumeration.4', { msg: $t('components.requests.bw_cloud_os.ram') })"
                      :description="$t('components.requests.bw_cloud_os.ram_description')"
                      label-for="ram_size">
          <b-form-input v-model="ram_size" :state="ram_size >= 1" type="number" min="1" step="1" required/>
        </b-form-group>
        <b-form-group :label="$t('global.enumeration.5', { msg: $t('components.requests.bw_cloud_os.volumes') })"
                      :description="$t('components.requests.bw_cloud_os.volumes_description')"
                      label-for="volumes">
          <b-form-input v-model="volumes" :state="volumes >= 10" type="number" min="10" step="1" required/>
        </b-form-group>
        <b-form-group :label="$t('global.enumeration.6', { msg: $t('components.requests.bw_cloud_os.disk_space') })"
                      :description="$t('components.requests.bw_cloud_os.disk_space_description')"
                      label-for="disk_size">
          <b-form-input v-model="disk_size" :state="disk_size >= 100 && disk_size % 10 === 0" type="number" min="100"
                        step="10" required/>
        </b-form-group>
        <b-form-group :label="$t('global.enumeration.7', { msg: $t('components.requests.bw_cloud_os.instances') })"
                      :description="$t('components.requests.bw_cloud_os.instances_description')"
                      label-for="instance_count">
          <b-form-input v-model="instance_count" type="number" min="1" step="1" :state="instance_count >= 1"/>
        </b-form-group>
        <b-alert show variant="info">
          <b-row>
            <b-col cols="auto" class="my-auto">
              <netvs-icon icon="info" size="2x"></netvs-icon>
            </b-col>
            <b-col class="h-100 my-auto">
              <p class="my-auto">{{ $t('components.requests.bw_cloud_os.optional_fields_note') }}</p>
            </b-col>
          </b-row>
        </b-alert>
        <b-form-group :label="$t('global.enumeration.8', { msg: $t('components.requests.bw_cloud_os.cut_off_date') })"
                      :description="$t('components.requests.bw_cloud_os.cut_off_date_description')"
                      label-for="cut_off_date"
        >
          <b-form-datepicker
            v-model="cut_off_date"
            :state="true"
            :min="minDate"
            :initial-date="initialDate"
            :label-no-date-selected="$t('system.datepicker.no_date_selected')"
            :label-help="$t('system.datepicker.arrow_key_support')" :locale="$i18n.locale"
            :label-current-month="$t('system.datepicker.current_month')"
            :label-next-month="$t('system.datepicker.next_month')"
            :label-next-year="$t('system.datepicker.next_year')"
            :label-prev-month="$t('system.datepicker.previous_month')"
            :label-prev-year="$t('system.datepicker.previous_year')"
            reset-button :label-today-button="$t('system.datepicker.select_today')"
            :label-reset-button="$t('system.datepicker.reset')"/>
        </b-form-group>
        <b-form-group :label="$t('global.enumeration.9', { msg: $t('components.requests.bw_cloud_os.backups') })"
                      :description="$t('components.requests.bw_cloud_os.backups_description')"
                      label-for="backups"
        >
          <b-form-input v-model="backups" type="number" min="30" step="1" :state="backups >= 30"/>
        </b-form-group>
        <b-form-group :label="$t('global.enumeration.10', { msg: $t('components.requests.bw_cloud_os.backup_size') })"
                      :description="$t('components.requests.bw_cloud_os.backup_size_description')"
                      label-for="backup_size"
        >
          <b-form-input v-model="backup_size" type="number" min="300" step="10"
                        :state="backup_size >= 300 && backup_size % 10 === 0"/>
        </b-form-group>
        <b-form-group :label="$t('global.enumeration.11', { msg: $t('components.requests.bw_cloud_os.snapshots') })"
                      :description="$t('components.requests.bw_cloud_os.snapshots_description')"
                      label-for="snapshots"
        >
          <b-form-input v-model="snapshots" type="number" min="10" step="1" :state="snapshots >= 10"/>
        </b-form-group>
        <b-form-group
          :label="$t('global.enumeration.12', { msg: $t('components.requests.bw_cloud_os.floating_ip_addresses') })"
          label-for="floating_ip_addresses">
          <b-form-input v-model="floating_ip_addresses" type="number" min="0" step="1"
                        :state="floating_ip_addresses >= 0"/>
        </b-form-group>
        <b-form-group :label="$t('global.enumeration.13', { msg: $t('components.requests.bw_cloud_os.networks') })"
                      :description="$t('components.requests.bw_cloud_os.networks_description')"
                      label-for="networks">
          <b-form-input v-model="networks" type="number" min="10" step="1" :state="networks >= 10"/>
        </b-form-group>
        <b-form-group :label="$t('global.enumeration.14', { msg: $t('components.requests.bw_cloud_os.subnets') })"
                      :description="$t('components.requests.bw_cloud_os.subnets_description')"
                      label-for="subnets">
          <b-form-input v-model="subnets" type="number" min="10" step="1" :state="subnets >= 10"/>
        </b-form-group>
        <b-form-group :label="$t('global.enumeration.15', { msg: $t('components.requests.bw_cloud_os.routers') })"
                      :description="$t('components.requests.bw_cloud_os.routers_description')"
                      label-for="routers">
          <b-form-input v-model="routers" type="number" min="1" step="1" :state="routers >= 1"/>
        </b-form-group>
        <b-form-group
          :label="$t('global.enumeration.16', { msg: $t('components.requests.bw_cloud_os.security_groups') })"
          :description="$t('components.requests.bw_cloud_os.security_groups_description')"
          label-for="security_groups">
          <b-form-input v-model="security_groups" type="number" min="20" step="1" :state="security_groups >= 20"/>
        </b-form-group>
      </b-form>
    </b-overlay>
    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="reset()">
        {{ $t('components.requests.all.reset_request') }}
      </b-button>
      <b-button variant="secondary" @click="cancel()">
        {{ $t('components.requests.all.cancel') }}
      </b-button>
      <b-button variant="primary" @click="ok()" :disabled="!readyForSubmission">
        {{ $t('components.requests.all.send') }}
        <b-spinner small v-show="sending"/>
        <netvs-icon v-show="status_icon !== null" :icon="status_icon"></netvs-icon>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import RequestsService from '@/api-services/requests.service'

export default {
  name: 'bwCloudOSRequestForm',
  data() {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    // 15th two months prior
    const minDate = new Date(today)
    minDate.setDate(today.getDate() + 1)
    const initialDate = new Date(today)
    initialDate.setMonth(today.getMonth() + 6)
    initialDate.setDate(today.getDate() + 1)

    return {
      error_message: null,
      sending: false,
      status_icon: null,
      project_name: '',
      motivation: '',
      virtual_cpu_count: 0,
      ram_size: 0,
      disk_size: 0,
      instance_count: 1,
      cut_off_date: null,
      backups: 30,
      backup_size: 300,
      snapshots: 10,
      floating_ip_addresses: 0,
      networks: 10,
      subnets: 10,
      routers: 1,
      security_groups: 20,
      volumes: 0,
      minDate: minDate,
      initialDate: initialDate
    }
  },
  computed: {
    readyForSubmission() {
      return (this.project_name.trim().length > 0 &&
        !this.project_name.includes('_') &&
        this.motivation.trim().length > 0 &&
        this.virtual_cpu_count >= 1 &&
        this.ram_size >= 1 &&
        this.volumes >= 10 &&
        this.disk_size >= 100 &&
        this.disk_size % 10 === 0 &&
        this.instance_count >= 1 &&
        this.backups >= 30 &&
        this.backup_size >= 300 &&
        this.backup_size % 10 === 0 &&
        this.snapshots >= 10 &&
        this.floating_ip_addresses >= 0 &&
        this.networks >= 10 &&
        this.subnets >= 10 &&
        this.routers >= 1 &&
        this.security_groups >= 20
      )
    },
    checkProjectName() {
      return this.project_name.trim().length > 0 && !this.project_name.includes('_')
    },
  },
  methods: {
    clear() {
      this.project_name = ''
      this.motivation = ''
      this.virtual_cpu_count = 0
      this.ram_size = 0
      this.disk_size = 0
      this.instance_count = 1
      this.floating_ip_addresses = 0
      this.cut_off_date = null
      this.backups = 30
      this.backup_size = 300
      this.snapshots = 10
      this.networks = 10
      this.subnets = 10
      this.routers = 1
      this.volumes = 0
      // Clear the error message
      this.error_message = null
      // Clear icon
      this.status_icon = null
    },
    reset() {
      this.clear()
    },
    sendRequest(e) {
      e.preventDefault()
      this.status_icon = null
      this.sending = true
      this.error_message = null
      RequestsService.send_bw_cloud_os_quota_request(this.$store.state, {
        project_name: this.project_name.trim(),
        motivation: this.motivation.trim(),
        virtual_cpu_count: this.virtual_cpu_count,
        ram_size: this.ram_size,
        disk_size: this.disk_size,
        instance_count: this.instance_count,
        backups: this.backups,
        backup_size: this.backup_size,
        snapshots: this.snapshots,
        floating_ip_addresses: this.floating_ip_addresses,
        networks: this.networks,
        subnets: this.subnets,
        routers: this.routers,
        security_groups: this.security_groups,
        volumes: this.volumes,
        cut_off_date: this.cut_off_date,
      }).then(response => {
        this.sending = false
        this.status_icon = 'request_sent'
        this.clear()
        // close modal after 1 second
        setTimeout(() => {
          this.$bvModal.hide('bw-cloud-os-request-form')
        }, 1000)
      }).catch(error => {
        window.console.log(error)
        this.sending = false
        this.status_icon = 'transaction_error'
        if (error.response.status >= 400 && error.response.status < 500) {
          this.error_message = error.response.data.detail
        }
      })
    }
  }
}
</script>

<style scoped>

</style>
